import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../../../lib/supabase';
import { log } from '../../../lib/logger';
import { useAuthStore } from '../../../stores/authStore';
import type { Conversation } from '../types';

type Filter = 'all' | 'clients' | 'team';

export function useConversations() {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const businessId = business?.id;
  const currentUserId = user?.id;
  const mountedRef = useRef(true);

  const [raw, setRaw] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [unreadMap, setUnreadMap] = useState<Record<string, number>>({});

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const fetchConversations = useCallback(async () => {
    if (!businessId || !currentUserId) { setLoading(false); return; }
    try {
      // Names come from conversations.participant_{1,2}_name snapshots (kept fresh by
      // the on_user_name_update trigger). Roles are not snapshotted — fetch them in a
      // second query keyed on the resolved other-participant IDs so the team/clients
      // filter and ThreadHeader role label keep working.
      const { data, error: err } = await supabase
        .from('conversations')
        .select('id, business_id, participant_1, participant_2, participant_1_name, participant_2_name, last_message_at, last_message_preview, shelved, shelved_at, created_at')
        .eq('business_id', businessId)
        .order('last_message_at', { ascending: false, nullsFirst: false });
      if (err) throw err;
      if (!mountedRef.current) return;

      // First pass: compute other participant from snapshot.
      const otherIds: string[] = [];
      const provisional: Conversation[] = (data ?? []).map((c: Record<string, unknown>) => {
        const isP1 = c.participant_1 === currentUserId;
        const otherId = (isP1 ? c.participant_2 : c.participant_1) as string;
        const storedName = (isP1 ? c.participant_2_name : c.participant_1_name) as string | null;
        if (!storedName) {
          log.warn('[admin useConversations] NULL participant snapshot for conversation', c.id);
        }
        otherIds.push(otherId);
        return {
          ...c,
          otherParticipantName: storedName ?? 'Unknown',
          otherParticipantId: otherId,
          otherParticipantRole: null,
          unreadCount: 0,
        } as Conversation;
      });

      // Second pass: resolve roles for filter + header label.
      const roleMap: Record<string, string> = {};
      if (otherIds.length > 0) {
        const { data: roleRows } = await supabase
          .from('users')
          .select('id, role')
          .in('id', otherIds);
        for (const row of roleRows ?? []) {
          roleMap[(row as { id: string }).id] = (row as { role: string }).role;
        }
      }
      const convs = provisional.map((c) => ({
        ...c,
        otherParticipantRole: roleMap[c.otherParticipantId] ?? null,
      }));

      // Fetch unread counts
      const { data: unreadData } = await supabase
        .from('messages')
        .select('sender_id')
        .eq('recipient_id', currentUserId)
        .eq('message_type', 'direct')
        .is('read_at', null);
      if (!mountedRef.current) return;

      const countMap: Record<string, number> = {};
      for (const m of unreadData ?? []) {
        countMap[m.sender_id] = (countMap[m.sender_id] ?? 0) + 1;
      }
      setUnreadMap(countMap);

      for (const c of convs) {
        c.unreadCount = countMap[c.otherParticipantId] ?? 0;
      }

      setRaw(convs);
    } catch (e) {
      if (mountedRef.current) setError(e instanceof Error ? e.message : 'Failed to load conversations');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [businessId, currentUserId]);

  useEffect(() => { void fetchConversations(); }, [fetchConversations]);

  // Realtime: refetch on conversation or message changes
  useEffect(() => {
    if (!businessId) return;
    const ch = supabase
      .channel(`admin-msgs-${businessId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'conversations', filter: `business_id=eq.${businessId}` }, () => { void fetchConversations(); })
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `business_id=eq.${businessId}` }, () => { void fetchConversations(); })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [businessId, fetchConversations]);

  // Filtered + searched conversations
  const conversations = useMemo(() => {
    let list = raw;
    if (filter === 'team') list = list.filter(c => c.otherParticipantRole === 'employee' || c.otherParticipantRole === 'manager');
    if (filter === 'clients') list = list.filter(c => c.otherParticipantRole !== 'employee' && c.otherParticipantRole !== 'manager' && c.otherParticipantRole !== 'owner');
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(c => c.otherParticipantName.toLowerCase().includes(q));
    }
    // Non-shelved first
    return [...list].sort((a, b) => {
      if (a.shelved && !b.shelved) return 1;
      if (!a.shelved && b.shelved) return -1;
      return 0;
    });
  }, [raw, filter, searchQuery]);

  const totalUnread = useMemo(() => {
    return Object.values(unreadMap).reduce((s, c) => s + c, 0);
  }, [unreadMap]);

  const shelveConversation = useCallback(async (id: string) => {
    await supabase.from('conversations').update({ shelved: true, shelved_at: new Date().toISOString() }).eq('id', id);
    void fetchConversations();
  }, [fetchConversations]);

  const unshelveConversation = useCallback(async (id: string) => {
    await supabase.from('conversations').update({ shelved: false, shelved_at: null }).eq('id', id);
    void fetchConversations();
  }, [fetchConversations]);

  const markAsRead = useCallback(async (conversationId: string) => {
    if (!currentUserId) return;
    const conv = raw.find(c => c.id === conversationId);
    if (!conv) return;
    await supabase
      .from('messages')
      .update({ read_at: new Date().toISOString() })
      .eq('recipient_id', currentUserId)
      .eq('sender_id', conv.otherParticipantId)
      .eq('message_type', 'direct')
      .is('read_at', null);
    void fetchConversations();
  }, [currentUserId, raw, fetchConversations]);

  return {
    conversations, loading, error,
    searchQuery, setSearchQuery,
    filter, setFilter,
    activeConversationId, setActiveConversationId,
    totalUnread,
    shelveConversation, unshelveConversation, markAsRead,
  };
}
